import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Skeleton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Divider,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack,
  EmojiEvents,
  TrendingUp,
  Redeem,
  Star,
  Timeline
} from '@mui/icons-material';
import axios from 'axios';

const PointsHistory = () => {
  const [pointsData, setPointsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPointsData();
  }, []);

  const fetchPointsData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('customerToken');

      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await axios.get('/api/customer/points', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setPointsData(response.data);
      }
    } catch (err) {
      setError('Failed to load points data');
      console.error('Error fetching points:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTierProgress = () => {
    if (!pointsData?.points) return 0;

    const { current_balance, tier } = pointsData.points;
    let nextTierThreshold = 0;

    switch (tier?.toLowerCase()) {
      case 'bronze':
        nextTierThreshold = 1000;
        break;
      case 'silver':
        nextTierThreshold = 5000;
        break;
      case 'gold':
        nextTierThreshold = 10000;
        break;
      case 'platinum':
        return 100; // Max tier
      default:
        nextTierThreshold = 1000;
    }

    return Math.min((current_balance / nextTierThreshold) * 100, 100);
  };

  const getNextTier = () => {
    const tier = pointsData?.points?.tier?.toLowerCase() || 'bronze';
    switch (tier) {
      case 'bronze': return 'Silver';
      case 'silver': return 'Gold';
      case 'gold': return 'Platinum';
      default: return 'Max';
    }
  };

  const getTierColor = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'bronze': return '#CD7F32';
      case 'silver': return '#C0C0C0';
      case 'gold': return '#FFD700';
      case 'platinum': return '#E5E4E2';
      default: return '#CD7F32';
    }
  };

  const formatTransactionType = (type) => {
    switch (type) {
      case 'earned': return 'Earned';
      case 'redeemed': return 'Redeemed';
      case 'expired': return 'Expired';
      case 'bonus': return 'Bonus';
      default: return type;
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'earned': return '➕';
      case 'redeemed': return '🎁';
      case 'expired': return '⏰';
      case 'bonus': return '🎉';
      default: return '📝';
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Points History</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Skeleton variant="text" height={40} />
                <Skeleton variant="text" height={60} />
                <Skeleton variant="rectangular" height={20} sx={{ mt: 2 }} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Skeleton variant="text" height={40} />
                <Skeleton variant="rectangular" height={300} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => window.location.href = '/profile'}
          sx={{ mb: 3 }}
        >
          Back to Profile
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const { points, transactions } = pointsData || {};

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => window.location.href = '/profile'}
          variant="outlined"
        >
          Back to Profile
        </Button>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          Loyalty Points
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Points Summary */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  mx: 'auto',
                  mb: 2,
                  bgcolor: getTierColor(points?.tier),
                  fontSize: '2rem'
                }}
              >
                <EmojiEvents />
              </Avatar>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {points?.current_balance || 0}
              </Typography>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Available Points
              </Typography>
              <Chip
                label={points?.tier || 'Bronze'}
                sx={{
                  bgcolor: getTierColor(points?.tier),
                  color: 'white',
                  fontWeight: 600
                }}
              />
            </CardContent>
          </Card>

          {/* Tier Progress */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUp />
                Tier Progress
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {points?.tier === 'platinum' ? 'Maximum tier reached!' : `${getNextTier()} tier at ${points?.tier === 'bronze' ? '1,000' : points?.tier === 'silver' ? '5,000' : '10,000'} points`}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={getTierProgress()}
                sx={{ height: 8, borderRadius: 4, mb: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                {points?.tier === 'platinum' ? 'Congratulations!' : `${Math.round(getTierProgress())}% to next tier`}
              </Typography>
            </CardContent>
          </Card>

          {/* Stats */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Statistics</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Total Earned:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {points?.total_earned || 0}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Total Redeemed:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {points?.total_redeemed || 0}
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Available Balance:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {points?.current_balance || 0}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Transaction History */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Timeline />
                Transaction History
              </Typography>

              {transactions && transactions.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Points</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((transaction) => (
                        <TableRow key={transaction.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1">
                                {getTransactionIcon(transaction.transaction_type)}
                              </Typography>
                              <Typography variant="body2">
                                {formatTransactionType(transaction.transaction_type)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {transaction.description}
                            </Typography>
                            {transaction.order_id && (
                              <Typography variant="caption" color="text.secondary">
                                Order #{transaction.order_id}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: transaction.points > 0 ? 'success.main' : 'error.main'
                              }}
                            >
                              {transaction.points > 0 ? '+' : ''}{transaction.points}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(transaction.created_at).toLocaleDateString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(transaction.created_at).toLocaleTimeString()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Star sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No transactions yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Start shopping to earn your first points!
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Redemption Options */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Redeem />
                How to Use Points
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Redeem your points for discounts on future purchases
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  label="₹10 off for 100 points"
                  variant="outlined"
                  color="primary"
                  sx={{ mb: 1 }}
                />
                <Chip
                  label="₹50 off for 500 points"
                  variant="outlined"
                  color="primary"
                  sx={{ mb: 1 }}
                />
                <Chip
                  label="₹100 off for 1000 points"
                  variant="outlined"
                  color="primary"
                  sx={{ mb: 1 }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Points can be redeemed during checkout
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PointsHistory;